import { effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { GoogleTagManagerService, GtmBaseEvent, GtmEvent, LoggerService } from '@aruba/common-lib';
import { TranslocoService } from '@jsverse/transloco';
import { User } from '@models';
import { AppConfigService } from '@services/app-config.service';
import { filter, Subscription } from 'rxjs';
import sha from 'sha.js';
import { countDaysSinceDate } from '../../shared/utils';
import { RoutesEnum } from '../enums/routes.enum';

interface GtmEventPage {
  url: string;
  title: string;
  referrer?: string;
}

export interface GtmPageviewUser {
  login_state: 'guest';
}

export interface GtmPageviewUserLogged extends Omit<GtmPageviewUser, 'login_state'> {
  login_state: 'logged_in';
  login_aruba: string;
  id: string;
  mail: string;
  first_name: string;
  last_name: string;
}

@Injectable({
  providedIn: 'root',
})
export class SupervisoreGTMHandler implements OnDestroy {
  private readonly router = inject(Router);
  private readonly loggerService = inject(LoggerService);
  private readonly translocoService = inject(TranslocoService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly googleTagManagerService = inject(GoogleTagManagerService);

  private previousUrl: string = document.referrer;

  private subscription = new Subscription();

  readonly user = signal<User | undefined>(undefined);

  constructor() {
    effect(() => {
      const user = this.user();
      if (user) {
        this.initGTM();
      }
    });
  }

  private initGTM() {
    const configGtm = this.appConfigService.getConfig()?.gtm;

    if (configGtm?.enabled) {
      this.googleTagManagerService.init(configGtm.id, this.getBaseDataLayerEvent());
      this.updateGtmOnRouteChange();
    } else {
      this.loggerService.warn(
        '⚠️ Google Tag Manager is disabled. No GTM script will be injected on this page.'
      );
    }
  }

  private getBaseDataLayerEvent(): GtmBaseEvent {
    const environment = this.appConfigService.getConfig()?.environment.toLowerCase();
    const gtmPageViewUser: GtmPageviewUser | GtmPageviewUserLogged = this.getUserInfo();
    const lang = this.translocoService.getActiveLang();

    const user = this.user();

    return {
      environment: environment!,
      page_type: 'site',
      platform: 'web',
      order_type: '',
      lang,
      user: gtmPageViewUser,
      usabilla: {
        user_created_at: user?.createdAt ? countDaysSinceDate(user?.createdAt) : undefined,
        pec_delegations_number: user?.pecDelegationsNumber,
        fel_delegations_number: user?.felDelegationsNumber,
        '2fa_status': user?.status2fa,
        sales_channel: user?.salesChannel,
        environment: this.appConfigService.getConfig()?.environment,
      },
    };
  }

  private getTitleByRoute(): string {
    const url = this.router.url;
    if (url === '/') {
      return 'Home';
    }

    return url.replace(/[/?=]/g, ' ').substring(1);
  }

  private updateGtmOnRouteChange() {
    if (!this.appConfigService.getConfig()?.gtm?.enabled) {
      return;
    }

    const events$ = this.router.events
      .pipe(filter((evt: any) => evt instanceof NavigationEnd))
      .pipe(
        filter((evt: any) => {
          /** Remove the URLs used for Keycloak / login redirect */
          const url = evt.url.split('?')[0];
          return url !== `/${RoutesEnum.AUTH_REDIRECT}` && url !== `/${RoutesEnum.SIGN_IN}`;
        })
      )
      .subscribe(evt => {
        const previousUrl = `${this.previousUrl}`;
        const currentUrl = `${window.location.protocol}//${window.location.hostname}/#${evt.urlAfterRedirects}`;
        const gtmTag = this.getGtmRouteEvent(previousUrl, currentUrl, this.getTitleByRoute());
        this.previousUrl = currentUrl;

        this.loggerService.info('GTM_PUSH_TAG', gtmTag);

        this.googleTagManagerService.pushTag(gtmTag);
      });

    this.subscription.add(events$);
  }

  getGtmRouteEvent(previousUrl: string, currentUrl: string, title: string): GtmEvent {
    const environment = this.appConfigService.getConfig()?.environment.toLowerCase();
    const user: GtmPageviewUser | GtmPageviewUserLogged = this.getUserInfo();
    const page: GtmEventPage = {
      url: currentUrl,
      title,
      referrer: previousUrl,
    };
    const lang = this.translocoService.getActiveLang();

    return {
      event: 'pageview',
      environment,
      page_type: 'site',
      platform: 'web',
      lang,
      page,
      user,
    };
  }

  private getUserInfo(): GtmPageviewUser | GtmPageviewUserLogged {
    const user = this.user();

    if (!user?.id) {
      return { login_state: 'guest' };
    }

    return {
      login_state: 'logged_in',
      login_aruba: user.username ?? '',
      id: sha('sha256')
        .update(user.owner?.email ?? '')
        .digest('hex'),
      mail: user.owner?.email ?? '',
      first_name: user.owner?.name ?? '',
      last_name: user.owner?.surname ?? '',
    };
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
